import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/base/Toast';

function nowText() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function MaterialWithdrawPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [amount, setAmount] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [note, setNote] = useState('');
  const [availableBalance, setAvailableBalance] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const inputAmount = parseFloat(amount) || 0;

  const loadBalance = useCallback(async () => {
    setLoaded(false);
    setLoadFailed(false);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setLoadFailed(true);
        return;
      }
      const { data } = await supabase
        .from('account_summary')
        .select('available_margin')
        .eq('user_id', user.id);
      const total = (data || []).reduce((sum, row) => sum + Number(row.available_margin || 0), 0);
      setAvailableBalance(total);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setAmount(val);
    }
  };

  const canSubmit = () => {
    return (
      inputAmount > 0 &&
      inputAmount <= availableBalance &&
      companyName.trim().length > 0 &&
      contactName.trim().length > 0 &&
      contactPhone.trim().length > 0 &&
      companyAddress.trim().length > 0
    );
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('登录已失效');

      // 扣除可用资金
      const { data: summary } = await supabase
        .from('account_summary')
        .select('available_margin')
        .eq('user_id', user.id)
        .maybeSingle();
      const currentAvailable = Number(summary?.available_margin) || 0;
      if (currentAvailable < inputAmount) {
        throw new Error('可用资金不足，请刷新后重试');
      }
      const { error: updateErr } = await supabase
        .from('account_summary')
        .update({ available_margin: currentAvailable - inputAmount })
        .eq('user_id', user.id);
      if (updateErr) throw updateErr;

      // 写入出金记录
      const remark = JSON.stringify({
        companyName: companyName.trim(),
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        companyAddress: companyAddress.trim(),
        note: note.trim(),
      });

      const { error } = await supabase.from('withdraw_records').insert({
        user_id: user.id,
        amount: inputAmount,
        method: '以料出金',
        status: 'pending',
        remark,
        create_time: nowText(),
      });
      if (error) throw error;

      // 通知管理员（不阻塞提交）
      supabase.functions.invoke('send-withdraw-email', {
        body: {
          userId: user.id,
          type: 'material',
          amount: inputAmount,
          companyName: companyName.trim(),
          contactName: contactName.trim(),
          contactPhone: contactPhone.trim(),
          companyAddress: companyAddress.trim(),
          createTime: nowText(),
        },
      }).catch(() => {});

      setSubmitted(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '提交失败', 'error');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-brown-50 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <i className="ri-check-line text-green-500 text-4xl"></i>
        </div>
        <h2 className="text-brown-900 text-lg font-bold mb-2">出金申请已提交</h2>
        <p className="text-brown-500 text-sm text-center">
          ¥{inputAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} 以料出金
        </p>
        <p className="text-brown-400 text-xs mt-2">我们将在10分钟内完成审核</p>
        <button
          onClick={() => {
            setSubmitted(false);
            setAmount('');
            setCompanyName('');
            setContactName('');
            setContactPhone('');
            setCompanyAddress('');
            setNote('');
          }}
          className="mt-6 px-6 py-2.5 rounded-full bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          继续出金
        </button>
        <button
          onClick={() => navigate('/profile/transactions')}
          className="mt-3 text-gold-500 text-sm cursor-pointer"
        >
          查看出金记录
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brown-50 pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">以料出金</span>
        <div className="w-8" />
      </div>

      {/* 顶部提示条 */}
      <div className="mx-3 mt-3 bg-amber-50 rounded-xl p-3.5 border border-amber-100 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500">
            <i className="ri-coins-line text-sm"></i>
          </span>
        </div>
        <p className="text-brown-700 text-sm leading-relaxed">
          以料出金将以实物黄金形式提取，提交申请后，<span className="text-amber-700 font-bold">10分钟内</span>完成审核
        </p>
      </div>

      {loadFailed ? (
        <div className="mx-3 mt-3 bg-white rounded-xl p-8 text-center">
          <i className="ri-error-warning-line text-brown-300 text-4xl"></i>
          <p className="text-brown-500 text-sm mt-3">数据加载失败，请检查登录状态</p>
          <button
            onClick={loadBalance}
            className="mt-4 px-5 py-2 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            重新加载
          </button>
        </div>
      ) : !loaded ? (
        <div className="flex items-center justify-center py-24">
          <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
        </div>
      ) : (
        <div className="px-3 mt-3 space-y-3">
          {/* 步骤1：出金金额 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-gold-500 text-brown-950 text-xs font-bold flex items-center justify-center flex-shrink-0">
                1
              </span>
              <h3 className="text-brown-900 text-sm font-bold">请选择要出金的金额</h3>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-brown-700 text-sm flex-shrink-0">出金金额（元）</span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                placeholder="请输入出金金额"
                className="flex-1 text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors text-right"
              />
            </div>
            <div className="flex items-center justify-end mt-2">
              <p className="text-brown-500 text-xs">
                可出金额：
                <span className="text-amber-600 font-medium">
                  ¥ {availableBalance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </p>
            </div>
          </div>

          {/* 步骤2：接收板料的公司 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-gold-500 text-brown-950 text-xs font-bold flex items-center justify-center flex-shrink-0">
                2
              </span>
              <h3 className="text-brown-900 text-sm font-bold">选择接收板料的公司</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-brown-700 text-sm flex-shrink-0 w-24">板料公司名称</span>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="请输入公司名称"
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-brown-700 text-sm flex-shrink-0 w-24">公司联系人</span>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="请输入联系人姓名"
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-brown-700 text-sm flex-shrink-0 w-24">联系电话</span>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="请输入联系电话"
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-brown-700 text-sm flex-shrink-0 w-24">公司地址</span>
                <input
                  type="text"
                  value={companyAddress}
                  onChange={(e) => setCompanyAddress(e.target.value)}
                  placeholder="请输入详细地址"
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
                />
                <span className="w-6 h-6 flex items-center justify-center text-brown-400 flex-shrink-0">
                  <i className="ri-map-pin-line text-sm"></i>
                </span>
              </div>
            </div>
          </div>

          {/* 步骤3：其他说明（选填） */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-gold-500 text-brown-950 text-xs font-bold flex items-center justify-center flex-shrink-0">
                3
              </span>
              <h3 className="text-brown-900 text-sm font-bold">其他说明（选填）</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-brown-700 text-sm flex-shrink-0 w-24">备注信息</span>
              <textarea
                value={note}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setNote(e.target.value);
                  }
                }}
                placeholder="请输入备注信息"
                rows={3}
                className="flex-1 text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors resize-none"
              />
            </div>
            <div className="flex justify-end mt-1">
              <span className="text-brown-400 text-xs">{note.length}/500</span>
            </div>
          </div>

          {/* 底部提示 */}
          <div className="bg-amber-50 rounded-xl p-3.5 border border-amber-100 flex items-start gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-amber-500 mt-0.5 flex-shrink-0">
              <i className="ri-error-warning-line text-sm"></i>
            </span>
            <p className="text-brown-600 text-sm leading-relaxed">
              请确保以上信息真实有效，审核通过后我们将安排发货或通知您自提
            </p>
          </div>
        </div>
      )}

      {/* 提交按钮 */}
      <div className="px-3 mt-4 mb-8">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit() || submitting || !loaded}
          className={`w-full py-3.5 rounded-xl text-white text-sm font-semibold cursor-pointer transition-all ${
            canSubmit() && !submitting && loaded
              ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98] shadow-sm shadow-amber-500/20'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              提交中...
            </span>
          ) : (
            '提交审核'
          )}
        </button>
        <p className="text-brown-400 text-xs text-center mt-3">
          提交后，我们将在 10 分钟内完成审核
        </p>
      </div>
    </div>
  );
}